"use client";

import { notifications } from "@mantine/notifications";
import { MerchandiseActions } from "Components/Admin/AdminMerchandise/MerchandiseActions";
import { MerchandiseForm } from "Components/Admin/AdminMerchandise/MerchandiseForm";
import { MerchandiseTable } from "Components/Admin/AdminMerchandise/MerchandiseTable";
import { Header } from "Components/Admin/StyleComponent/Header";
import { useEffect, useState } from "react";
import {
  useAddMerchandiseMutation,
  useBulkDeleteMerchandisesMutation,
  useDeleteMerchandiseMutation,
  useGetAllMerchandisesQuery,
  useUpdateMerchandiseMutation,
} from "Services/Admin/MerchandiseApiSlice";
import { Merchandise } from "Types/Admin/AdminMerchandiseType";

const PLANT = process.env.REACT_APP_PLANT;

const AdminMerchandise = () => {
  const { data: apiMerchandises = [], isLoading } = useGetAllMerchandisesQuery({
    plant: PLANT || "",
  });

  const merchandises: Merchandise[] = apiMerchandises.map(
    (merchandise: Merchandise) => ({
      id: merchandise.id,
      merchandise: merchandise.merchandise,
      isActive: merchandise.isActive,
      crBy: merchandise.crBy,
      upBy: merchandise.upBy,
      createdAt: merchandise.createdAt,
      updatedAt: merchandise.updatedAt,
      plant: merchandise.plant,
    }),
  );

  const [addMerchandise] = useAddMerchandiseMutation();
  const [updateMerchandise] = useUpdateMerchandiseMutation();
  const [deleteMerchandise] = useDeleteMerchandiseMutation();

  const [bulkDeleteMerchandises, { isLoading: isBulkDeletingMerchandises }] =
  useBulkDeleteMerchandisesMutation();
  const [selectedMerchandises, setSelectedMerchandises] = useState<number[]>(
    [],
  );

  const [showForm, setShowForm] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [editingMerchandise, setEditingMerchandise] =
    useState<Merchandise | null>(null);
  const [selectedMerchandiseId, setSelectedMerchandiseId] = useState<
    number | null
  >(null);

  const [filteredMerchandises, setFilteredMerchandises] = useState<
    Merchandise[]
  >([]);
  const [filterOption, setFilterOption] = useState("All");
  const [searchText, setSearchText] = useState("");

  const handleTableSelect = (ids: number[]) => {
    setSelectedMerchandises(ids);
    setSelectedMerchandiseId(ids.length > 0 ? ids[0] : null);
  };

  const handleBulkDelete = async () => {
    if (selectedMerchandises.length === 0) return;

    try {
      const count = selectedMerchandises.length;
      const res = await bulkDeleteMerchandises({
        ids: selectedMerchandises,
        plant: PLANT || "",
      }).unwrap();

      notifications.show({
        message: res.message,
        color: "teal",
      });

      setSelectedMerchandises([]);
      setSelectedMerchandiseId(null);
    } catch (error: any) {
      let errorMessage = "Failed to delete merchandises";
      if (error.data?.message) {
        errorMessage = error.data.message;
      } else if (typeof error.data === "string" && error.data) {
        errorMessage = error.data;
      } else if (error.message) {
        errorMessage = error.message;
      }
      notifications.show({
        message: errorMessage,
        color: "red",
      });
      console.error("Failed to bulk delete merchandises:", error);
    }
  };


  useEffect(() => {
    let filtered = merchandises ? [...merchandises] : [];

    if (filterOption === "Active") {
      filtered = filtered.filter((merchandise) => merchandise.isActive === "Y");
    } else if (filterOption === "Inactive") {
      filtered = filtered.filter((merchandise) => merchandise.isActive !== "Y");
    }

    if (searchText) {
      filtered = filtered.filter((merchandise) =>
        merchandise.merchandise
          .trim()
          .toLowerCase()
          .includes(searchText.trim().toLowerCase()),
      );
    }

    const currentFilteredJSON = JSON.stringify(filtered);
    const previousFilteredJSON = JSON.stringify(filteredMerchandises);

    if (currentFilteredJSON !== previousFilteredJSON) {
      setFilteredMerchandises(filtered);
    }
  }, [merchandises, filterOption, searchText]);

  const handleViewMerchandise = async (id: number) => {
    const merchandiseToView = merchandises.find(
      (merchandise) => merchandise.id === id,
    );

    if (merchandiseToView) {
      setEditingMerchandise(merchandiseToView);
      setViewMode(true);
      setShowForm(true);
    }
  };

  const handleEditMerchandise = async (id: number) => {
    const merchandiseToEdit = merchandises.find(
      (merchandise) => merchandise.id === id,
    );

    if (merchandiseToEdit) {
      setEditingMerchandise(merchandiseToEdit);
      setViewMode(false);
      setShowForm(true);
    }
  };

  const handleSubmit = async (data: {
    merchandise: string;
    isActive: string;
  }) => {
    try {
      if (editingMerchandise) {
        const response = await updateMerchandise({
          id: editingMerchandise.id,
          plant: PLANT || "",
          data: {
            merchandise: data.merchandise,
            isActive: data.isActive,
            upBy: "admin",
          },
        }).unwrap();
        notifications.show({
          message: response.message,
          color: "teal",
        });
      } else {
        const addResponse = await addMerchandise({
          plant: PLANT || "",
          data: {
            merchandise: data.merchandise,
            isActive: data.isActive,
            crBy: "admin",
          },
        }).unwrap();
        notifications.show({
          message: addResponse.message,
          color: "teal",
        });
      }

      setShowForm(false);
      setEditingMerchandise(null);
    } catch (error: any) {
      let errorMessage = "Failed to save merchandise";
      if (error.data?.message) {
        errorMessage = error.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      notifications.show({
        message: errorMessage,
        color: "red",
      });
      console.error("Failed to save merchandise:", error);
    }

  };

  const handleDelete = async (id?: number) => {
    const merchandiseIdToDelete = id || selectedMerchandiseId;

    if (merchandiseIdToDelete) {
      try {
        const res = await deleteMerchandise({
          id: merchandiseIdToDelete,
          plant: PLANT || "",
        }).unwrap();

        notifications.show({
          message: res.message,
          color: "teal",
        });
        setSelectedMerchandiseId(null);
      } catch (error: any) {
        let errorMessage = "Failed to delete merchandise";
        if (error.data?.message) {
          errorMessage = error.data.message;
        } else if (typeof error.data === "string" && error.data) {
          errorMessage = error.data;
        } else if (error.message) {
          errorMessage = error.message;
        }
        notifications.show({
          message: errorMessage,
          color: "red",
        });
        console.error("Failed to delete merchandise:", error);
      }
    }
  };

  const merchandiseMappingFromDTOToRequest = (
    editingMerchandise: Merchandise | null,
  ) => {
    if (editingMerchandise != null) {
      return {
        merchandise: editingMerchandise.merchandise,
        isActive: editingMerchandise.isActive,
      };
    } else {
      return null;
    }
  };
if (isLoading && !showForm) {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="w-10 h-10 border-4 border-orange-400 border-t-transparent rounded-full animate-spin"></div>
    </div>
  );
}
  return (
    <div className="font-body p-6 mx-auto">
      {!showForm ? (
        <>
          <Header
            title="Merchandise"
            onAdd={() => setShowForm(true)}
            addLabel="Add Merchandise"
          />
          <div className="bg-white rounded-lg border shadow-sm overflow-hidden p-4 pt-8">
            <MerchandiseActions
              hideEdit={false}
              onDelete={handleBulkDelete}
              onFilterChange={setFilterOption}
              onSearchChange={setSearchText}
              isEditDisabled={!selectedMerchandiseId}
              isDeleteDisabled={!selectedMerchandiseId}
              isBulkDeleting={isBulkDeletingMerchandises}
            />
            <MerchandiseTable
              merchandises={filteredMerchandises}
              onSelectMerchandise={handleTableSelect}
              onDeleteMerchandise={(id) => handleDelete(id)}
              onViewMerchandise={handleViewMerchandise}
              onEditMerchandise={handleEditMerchandise}
              selectedMerchandise={selectedMerchandiseId}
              
            />
          </div>
        </>
      ) : (
        <>
          <button
            className="flex items-center gap-1 text-gray-500 hover:text-gray-700 mb-4"
            onClick={() => {
              setShowForm(false);
              setEditingMerchandise(null);
              setViewMode(false);
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-arrow-left-icon lucide-arrow-left h-4 w-4"
            >
              <path d="m12 19-7-7 7-7" />
              <path d="M19 12H5" />
            </svg>
            <span className="text-sm">Back</span>
          </button>
          <Header
            title={
              viewMode
                ? "View Merchandise"
                : editingMerchandise
                  ? "Edit Merchandise"
                  : "Add Merchandise"
            }
          />
          <MerchandiseForm
            initialValues={
              merchandiseMappingFromDTOToRequest(editingMerchandise) || {
                isActive: "Y",
                merchandise: "",
              }
            }
            onSubmit={handleSubmit}
            onCancel={() => {
              setShowForm(false);
              setViewMode(false);
              setEditingMerchandise(null);
            }}
            mode={viewMode ? "view" : editingMerchandise ? "edit" : "add"}
          />
        </>
      )}
    </div>
  );
};

export default AdminMerchandise;
